"use client";

import { useMemo, useState } from "react";

import EyeIcon from "@/icons/eye.svg";
import EyeShashIcon from "@/icons/eye-slash.svg";

import { IconSwitchButton } from "@/shared-ui/SwitchButton";
import { AppField } from "@/dls/AppField";

type BaseProps = React.ComponentProps<typeof AppField> & {
  isAutoComplete?: boolean;
};

const PasswordField: React.FC<BaseProps> = ({
  isAutoComplete = false,
  ...rest
}) => {
  const [inputType, setInputType] = useState("password");

  const isVisible = inputType !== "password";

  const handleChange = () => {
    setInputType(isVisible ? "password" : "text");
  };

  const passwordStyle = useMemo(() => {
    if (isAutoComplete) {
      return {
        ...rest.style,
        "text-security": inputType === "password" ? "disc" : "none",
        "-webkit-text-security": inputType === "password" ? "disc" : "none",
        "text-security-color": "transparent",
        "-webkit-text-security-color": "transparent",
      };
    }
    return rest.style;
  }, [inputType, isAutoComplete]);

  const inputId = useMemo(() => {
    if (isAutoComplete) {
      return `password_${Math.random()}`;
    }
    return rest.id;
  }, [inputType, isAutoComplete]);

  const inputTypeM = useMemo(() => {
    if (isAutoComplete) {
      return `text`;
    }
    return inputType;
  }, [inputType, isAutoComplete]);

  const suffix = (
    <IconSwitchButton
      iconSize={20}
      tabIndex={-1}
      checked={isVisible}
      CheckedIcon={EyeIcon}
      UncheckedIcon={EyeShashIcon}
      onCheckedChange={handleChange}
    />
  );

  return (
    <AppField
      type={inputTypeM}
      id={inputId}
      suffix={suffix}
      style={passwordStyle as React.CSSProperties}
      {...rest}
    />
  );
};

export { PasswordField };
